import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { deleteProduct, fetchProducts, updateProductDisplay } from "../../api/productApi";
import { fetchCategories } from "../../api/categoryApi";
import ModuleSearchControls, {
  ModuleFilterMenu,
  ModulePerPageMenu,
  ModuleRadioOption,
  ModuleSortMenu,
} from "../../components/ModuleSearchControls";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import PageAlert from "../../components/PageAlert/PageAlert";
import DataTable, { DataTableActions, DataTableFooter } from "../../components/DataTable";
import ProductsNav from "./components/ProductsNav";
import { resolveApiError } from "../../utils/apiError";
import "./products.scss";

const SORT_OPTIONS = [
  { value: "sort_order", label: "Display order" },
  { value: "created_at", label: "Created date" },
  { value: "name", label: "Name" },
  { value: "sku", label: "SKU" },
  { value: "featured", label: "Featured" },
];

const asFlag = (value) => {
  if (value === true || value === 1 || value === "1") return true;
  if (typeof value === "string") return !["0", "false", "no", ""].includes(value.toLowerCase());
  return false;
};

const imageUrl = (image) => {
  if (!image) return "";
  if (typeof image === "string") return image;
  return image.url || image.path || image.thumbnail_url || image.image_url || image.file?.url || "";
};

const productListImageUrl = (product) => {
  const images = [
    ...(Array.isArray(product?.images) ? product.images : []),
    ...(Array.isArray(product?.product_images) ? product.product_images : []),
  ];
  const thumbnails = images
    .filter((image) => asFlag(image?.is_thumbnail))
    .sort((left, right) => (Number(left?.sort_order) || 0) - (Number(right?.sort_order) || 0));
  const baseImages = images
    .filter((image) => asFlag(image?.is_base_image))
    .sort((left, right) => (Number(left?.sort_order) || 0) - (Number(right?.sort_order) || 0));
  return (
    imageUrl(thumbnails[0]) ||
    imageUrl(baseImages[0]) ||
    imageUrl(product?.thumbnail) ||
    product?.thumbnail_url ||
    product?.thumbnail_image_url ||
    imageUrl(product?.thumbnail_image) ||
    ""
  );
};

const ProductsPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [meta, setMeta] = useState(null);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [sortBy, setSortBy] = useState("sort_order");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [updatingIds, setUpdatingIds] = useState(() => new Set());
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [perPageOpen, setPerPageOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const sortOrderOriginalRef = useRef({});
  const categoriesLoaded = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const requests = [
        fetchProducts({
          search: debouncedQuery || undefined,
          category_id: categoryId || undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
          page,
          per_page: perPage,
        }),
      ];

      if (!categoriesLoaded.current) {
        requests.unshift(fetchCategories({ page: 1, per_page: 100 }));
      }

      const results = await Promise.all(requests);
      const categoryResult = categoriesLoaded.current ? null : results[0];
      const productData = categoriesLoaded.current ? results[0] : results[1];

      if (categoryResult) {
        setCategories(categoryResult.items);
        categoriesLoaded.current = true;
        setSummary({
          ...(productData.summary ?? {}),
          total_categories: categoryResult.meta?.total ?? categoryResult.items.length,
        });
      } else {
        setSummary((current) => ({
          ...(productData.summary ?? {}),
          total_categories: current?.total_categories ?? categories.length,
        }));
      }

      setProducts(productData.products ?? []);
      setMeta(productData.meta);
    } catch (requestError) {
      resolveApiError(requestError, setError, navigate);
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery, categoryId, sortBy, sortOrder, page, perPage, categories.length]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const setProductField = (id, field, value) => setProducts((items) => items.map((product) =>
    product.id === id ? { ...product, [field]: value } : product
  ));

  const adjustSummary = (field, delta) => {
    if (!summary || !delta) return;
    setSummary((current) => ({ ...current, [field]: (current[field] || 0) + delta }));
  };

  const persistDisplay = async (product, updates) => {
    const nextProduct = { ...product, ...updates };
    const previousProduct = product;

    setProducts((items) => items.map((item) =>
      item.id === product.id ? { ...item, ...updates } : item
    ));

    if (updates.visibility !== undefined && updates.visibility !== product.visibility) {
      adjustSummary("visible_products", updates.visibility ? 1 : -1);
    }
    if (updates.featured !== undefined && updates.featured !== product.featured) {
      adjustSummary("featured_products", updates.featured ? 1 : -1);
    }

    setUpdatingIds((ids) => new Set(ids).add(product.id));
    setError("");

    try {
      await updateProductDisplay(nextProduct);
    } catch (requestError) {
      setProducts((items) => items.map((item) =>
        item.id === product.id ? previousProduct : item
      ));
      if (updates.visibility !== undefined && updates.visibility !== product.visibility) {
        adjustSummary("visible_products", updates.visibility ? -1 : 1);
      }
      if (updates.featured !== undefined && updates.featured !== product.featured) {
        adjustSummary("featured_products", updates.featured ? -1 : 1);
      }
      resolveApiError(requestError, setError, navigate);
    } finally {
      setUpdatingIds((ids) => {
        const next = new Set(ids);
        next.delete(product.id);
        return next;
      });
    }
  };

  const toggleVisibility = (product) => {
    if (updatingIds.has(product.id)) return;
    persistDisplay(product, { visibility: !product.visibility });
  };

  const toggleFeatured = (product) => {
    if (updatingIds.has(product.id)) return;
    persistDisplay(product, { featured: !product.featured });
  };

  const saveSortOrder = (product, value) => {
    const sortOrder = Number(value) || 0;
    const original = sortOrderOriginalRef.current[product.id];
    delete sortOrderOriginalRef.current[product.id];

    if (sortOrder === Number(original ?? product.sort_order) || updatingIds.has(product.id)) return;
    persistDisplay(product, { sort_order: sortOrder });
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setError("");
    try {
      await deleteProduct(pendingDelete.id);
      setPendingDelete(null);
      setNotice("Product deleted.");
      await loadProducts();
    } catch (requestError) {
      resolveApiError(requestError, setError, navigate);
    } finally {
      setDeleting(false);
    }
  };

  const resetFilters = () => {
    setQuery("");
    setDebouncedQuery("");
    setCategoryId("");
    setSortBy("sort_order");
    setSortOrder("desc");
    setPerPage(20);
    setPage(1);
  };

  return (
    <div className="products-page">
      {notice && (
        <PageAlert variant="success" onDismiss={() => setNotice("")}>{notice}</PageAlert>
      )}
      {error && (
        <PageAlert variant="error" onDismiss={() => setError("")}>{error}</PageAlert>
      )}
      <section className="products-page__toolbar">
        <div>
          <h2>Product catalogue</h2>
          <p>Choose what customers see on the frontend and how products are featured.</p>
        </div>
        <button className="ui-button ui-button--primary" onClick={() => navigate("/admin/products/create")}>
          <Icon icon="ph:plus" height={14} /> Add product
        </button>
      </section>
      <ProductsNav />
      <section className="products-page__summary">
        <div>
          <strong>{summary?.total_products ?? "—"}</strong>
          <span>Products shown here</span>
        </div>
        <div>
          <strong>{summary?.visible_products ?? "—"}</strong>
          <span>Visible online</span>
        </div>
        <div>
          <strong>{summary?.featured_products ?? "—"}</strong>
          <span>Featured</span>
        </div>
        <div>
          <strong>{summary?.total_categories ?? "—"}</strong>
          <span>Total categories</span>
        </div>
      </section>
      <section className="products-page__card">
        <ModuleSearchControls value={query} onChange={setQuery} placeholder="Search product name or SKU">
          <ModuleFilterMenu
            open={filterOpen}
            onToggle={() => {
              setFilterOpen((open) => !open);
              setSortOpen(false);
              setPerPageOpen(false);
            }}
            onClose={() => setFilterOpen(false)}
            count={categoryId ? 1 : 0}
            columns={1}
            size="narrow"
            ariaLabel="Product filters"
          >
            <fieldset>
              <legend>Category</legend>
              <ModuleRadioOption
                name="product-category"
                checked={!categoryId}
                onChange={() => {
                  setCategoryId("");
                  setPage(1);
                }}
              >
                All categories
              </ModuleRadioOption>
              {categories.map((category) => (
                <ModuleRadioOption
                  key={category.id}
                  name="product-category"
                  checked={String(categoryId) === String(category.id)}
                  onChange={() => {
                    setCategoryId(String(category.id));
                    setPage(1);
                  }}
                >
                  {category.name}
                </ModuleRadioOption>
              ))}
            </fieldset>
          </ModuleFilterMenu>
          <ModuleSortMenu
            open={sortOpen}
            onToggle={() => {
              setSortOpen((open) => !open);
              setFilterOpen(false);
              setPerPageOpen(false);
            }}
            onClose={() => setSortOpen(false)}
            sortBy={sortBy}
            sortOrder={sortOrder}
            options={SORT_OPTIONS}
            onChangeSortBy={(value) => {
              setSortBy(value);
              setPage(1);
            }}
            onChangeSortOrder={(value) => {
              setSortOrder(value);
              setPage(1);
            }}
          />
          <ModulePerPageMenu
            open={perPageOpen}
            onToggle={() => {
              setPerPageOpen((open) => !open);
              setFilterOpen(false);
              setSortOpen(false);
            }}
            onClose={() => setPerPageOpen(false)}
            value={perPage}
            onChange={(value) => {
              setPerPage(value);
              setPage(1);
            }}
          />
          <button className="ui-button" type="button" onClick={resetFilters}>
            <Icon icon="ph:arrow-clockwise" height={14} /> Reset
          </button>
        </ModuleSearchControls>
        <DataTable
          minWidth={800}
          loading={loading}
          loadingMessage="Loading products…"
          emptyMessage="No products match your filters."
          rows={products}
          columns={[
            {
              key: "product",
              header: "Product",
              render: (product) => {
                const thumbUrl = productListImageUrl(product);
                return (
                  <div className="products-page__product">
                    {thumbUrl ? (
                      <img src={thumbUrl} alt="" />
                    ) : (
                      <span>{(product.name || "?").slice(0, 2).toUpperCase()}</span>
                    )}
                    <div>
                      <strong>{product.name}</strong>
                      <small>{product.sku}</small>
                    </div>
                  </div>
                );
              },
            },
            {
              key: "category",
              header: "Category",
              render: (product) => (
                <span className="products-page__category">{product.category?.name || "Uncategorised"}</span>
              ),
            },
            {
              key: "visibility",
              header: "Storefront",
              render: (product) => {
                const isUpdating = updatingIds.has(product.id);
                return (
                  <>
                    <button
                      className={`switch ${product.visibility ? "switch--on" : ""}`}
                      onClick={() => toggleVisibility(product)}
                      disabled={isUpdating}
                      aria-label={`Toggle ${product.name} visibility`}
                    >
                      <i />
                    </button>
                    <small className="state-label">{product.visibility ? "Visible" : "Hidden"}</small>
                  </>
                );
              },
            },
            {
              key: "featured",
              header: "Featured",
              render: (product) => {
                const isUpdating = updatingIds.has(product.id);
                return (
                  <button
                    className={`products-page__star ${product.featured ? "products-page__star--on" : ""}`}
                    onClick={() => toggleFeatured(product)}
                    disabled={isUpdating}
                    aria-label={`Toggle ${product.name} featured status`}
                  >
                    <Icon icon={product.featured ? "ph:star-fill" : "ph:star"} height={16} />
                  </button>
                );
              },
            },
            {
              key: "sort_order",
              header: "Display order",
              render: (product) => {
                const isUpdating = updatingIds.has(product.id);
                return (
                  <input
                    className="products-page__order"
                    type="number"
                    min="0"
                    value={product.sort_order}
                    disabled={isUpdating}
                    onFocus={() => {
                      sortOrderOriginalRef.current[product.id] = product.sort_order;
                    }}
                    onChange={(event) => setProductField(product.id, "sort_order", event.target.value)}
                    onBlur={(event) => saveSortOrder(product, event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") event.currentTarget.blur();
                    }}
                  />
                );
              },
            },
            {
              key: "actions",
              header: "Actions",
              render: (product) => (
                <DataTableActions>
                  <button type="button" onClick={() => navigate(`/admin/products/${product.id}`)}>
                    Edit
                  </button>
                  <button type="button" className="danger" onClick={() => setPendingDelete(product)}>
                    Delete
                  </button>
                </DataTableActions>
              ),
            },
          ]}
        />
        <DataTableFooter meta={meta} page={page} loading={loading} onPageChange={setPage} noun="products" />
      </section>
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete product"
        message={pendingDelete ? `Delete “${pendingDelete.name}”? This cannot be undone.` : ""}
        confirming={deleting}
        onCancel={() => !deleting && setPendingDelete(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default ProductsPage;
